import React from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { useMutation } from 'urql';
import Button from '../../../../components/buttons/button';
import { BlurredContainerM2030 } from '../../../../components/containers/containers';
import DataRow from '../../../../components/data-viewers/data-row/data-row';
import { formatDate } from '../../../../helpers/date-formatters';
import { formatNumber } from '../../../../helpers/number-formatters';
import { GraphqlOperations } from '../../../../logic/graphql/operations';
import { CreateExchangeOrder } from '../../../../logic/graphql/types/types';
import ExchangeOrders from '../../../../logic/redux/exchange-orders';
import { stringifyExchangeRateType } from '../../../../helpers/type-formatters';
import { FormattedMessage, useIntl } from 'react-intl';

const NewExchangeOrderDetails = (props: {
  onCreationSuccess: (token: string) => void;
  onBackClick: () => void;
}): JSX.Element | null => {
  const intl = useIntl();
  const { addToast } = useToasts();
  const exchangeOrder = useSelector(ExchangeOrders.select.selectEditedExchangeOrder);

  const [creationResult, createExchangeOrder] = useMutation(
    GraphqlOperations.mutations.create_exchange_order.mutationString,
  );

  if (!exchangeOrder || !exchangeOrder.securityDetails) {
    return null;
  }

  const handleCreateClick = (): void => {
    if (
      exchangeOrder &&
      exchangeOrder.direction &&
      exchangeOrder.account &&
      exchangeOrder.security !== undefined &&
      exchangeOrder.count !== undefined &&
      exchangeOrder.type &&
      exchangeOrder.validity_start
    ) {
      const createdExchangeOrder: CreateExchangeOrder = {
        edited_id: exchangeOrder.edited_id,
        direction: exchangeOrder.direction,
        account: exchangeOrder.account,
        security: exchangeOrder.security,
        count: exchangeOrder.count,
        daytrade: !!exchangeOrder.daytrade,
        daytrade_type: exchangeOrder.daytrade_type,
        type: exchangeOrder.type,
        limit_price: exchangeOrder.limit_price,
        activation_price: exchangeOrder.activation_price,
        outside_trading_hours: !!exchangeOrder.outside_trading_hours,
        validity_start: exchangeOrder.validity_start,
        validity_end: exchangeOrder.validity_end ? exchangeOrder.validity_end : null,
      };
      const variables = GraphqlOperations.mutations.create_exchange_order.createVars(
        createdExchangeOrder,
      );
      createExchangeOrder(variables)
        .then((result) => {
          if (result && !result.error && result.data?.create_exchange_order) {
            props.onCreationSuccess(result.data.create_exchange_order);
          } else {
            addToast(intl.formatMessage({ id: 'exchangeOrderFormPage.overview.toast.error' }), {
              appearance: 'error',
              autoDismiss: true,
            });
          }
          return true;
        })
        .catch(() =>
          addToast(intl.formatMessage({ id: 'exchangeOrderFormPage.overview.toast.error' }), {
            appearance: 'error',
            autoDismiss: true,
          }),
        );
    }
  };

  return (
    <>
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 md:grid-cols-2 mb-6">
        <div className="w-1/1">
          <BlurredContainerM2030 className="mb-1">
            <h2 className="mb-6">
              <FormattedMessage id="exchangeOrderFormPage.overview.overviewLabel" />
            </h2>
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderFormPage.overview.directionLabel' })}
              value={intl.formatMessage({ id: `order.direction.${exchangeOrder.direction}` })}
            />
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderFormPage.overview.accountLabel' })}
              value={exchangeOrder.account}
            />
            {/*<DataRow label="Daytrade" value={formatBoolean(exchangeOrder.daytrade)} />
            <DataRow
              label="Daytrade típusa"
              value={intl.formatMessage({ id: `order.dayTradeType.${exchangeOrder.daytrade_type}`) }
            />*/}
            <DataRow
              label={intl.formatMessage({
                id: 'exchangeOrderFormPage.overview.exchangeRateTypeLabel',
              })}
              value={stringifyExchangeRateType(exchangeOrder.type, intl)}
            />
          </BlurredContainerM2030>
          <BlurredContainerM2030>
            <h2 className="mb-6">
              <FormattedMessage id="exchangeOrderFormPage.overview.securityLabel" />
            </h2>
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderFormPage.overview.securityNameLabel' })}
              value={
                (exchangeOrder.securityDetails.name || '') +
                ' ' +
                intl.formatMessage({ id: 'exchangeOrderFormPage.overview.securityAffix' })
              }
            />
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderFormPage.overview.countLabel' })}
              value={formatNumber(
                exchangeOrder.count,
                intl.formatMessage({ id: 'exchangeOrderFormPage.overview.pcs' }),
              )}
            />

            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderFormPage.overview.currencyLabel' })}
              value={exchangeOrder.securityDetails.currency.currency}
            />
          </BlurredContainerM2030>
        </div>
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id="exchangeOrderFormPage.overview.detailsLabel" />
          </h2>
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderFormPage.overview.localityLabel' })}
            value={exchangeOrder.securityDetails.stock_market_locality}
          />
          <DataRow
            label={intl.formatMessage({
              id: 'exchangeOrderFormPage.overview.outsideTradingHoursLabel',
            })}
            value={intl.formatMessage({
              id: exchangeOrder.outside_trading_hours ? 'common.yes' : 'common.no',
            })}
          />
          <DataRow
            label={intl.formatMessage({
              id: 'exchangeOrderFormPage.overview.activationPriceLabel',
            })}
            value={formatNumber(
              exchangeOrder.activation_price,
              exchangeOrder.securityDetails.currency.currency,
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderFormPage.overview.limitPriceLabel' })}
            value={formatNumber(
              exchangeOrder.limit_price,
              exchangeOrder.securityDetails.currency.currency,
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderFormPage.overview.validityLabel' })}
            value={
              exchangeOrder.validity_end
                ? `${formatDate(exchangeOrder.validity_start)} - ${formatDate(
                    exchangeOrder.validity_end,
                  )}`
                : intl.formatMessage({ id: 'exchangeOrderFormPage.overview.toCancellation' })
            }
          />
        </BlurredContainerM2030>
      </div>
      <div className="flex flex-row">
        <Button secondary onClick={props.onBackClick} className="mr-3">
          <FormattedMessage id="exchangeOrderFormPage.overview.backButton" />
        </Button>
        <Button onClick={handleCreateClick} loading={creationResult.fetching}>
          <FormattedMessage id="exchangeOrderFormPage.overview.confirmButton" />
        </Button>
      </div>
    </>
  );
};

export default NewExchangeOrderDetails;
